import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './Products.css';
import { IMAGE_BASE_URL } from '../constant';

const Products = () => {
  const { currentLanguage } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/products?lang=${currentLanguage}`);
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        console.log('✅ Products loaded:', data);
        setCategories(data);
      } catch (err) {
        console.error('❌ Error loading products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentLanguage]);

  return (
    <div className="products">
      {/* Page Header */}
      <section className="page-header">
        <div className="header-overlay"></div>
        <div className="container">
          <div className="header-content">
            <h1>Our Products</h1>
            <p>Premium quality seeds for every farming need</p>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="section categories-section">
        <div className="container">
          <h2 className="section-title">Seed Categories</h2>
          <p className="section-subtitle">
            Explore our wide range of premium vegetable seeds
          </p>

          {loading && (
            <div className="loading-state">
              <div className="spinner">🌱</div>
              <p>Loading products...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>⚠️ {error}</p>
              <button onClick={() => window.location.reload()} className="btn-secondary">
                Retry
              </button>
            </div>
          )}

          {!loading && !error && categories.length === 0 && (
            <div className="empty-state">
              <p>No products available yet.</p>
            </div>
          )}

          {!loading && !error && categories.length > 0 && (
            <div className="categories-grid">
              {categories.map(category => (
                <Link 
                  key={category.id} 
                  to={`/products/${category.slug}`} 
                  className="category-card glass-card"
                >
                  {/* Show uploaded image if available, otherwise fall back to icon */}
                  {category.image_url ? (
                    <div className="category-img-wrap">
                      <img
                        src={`${IMAGE_BASE_URL}${category.image_url}`}
                        alt={category.name}
                        className="category-img"
                        onError={e => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="category-icon" style={{ display: 'none' }}>
                        {category.icon || '🌱'}
                      </div>
                    </div>
                  ) : (
                    <div className="category-icon">{category.icon || '🌱'}</div>
                  )}
                  <h3>{category.name || category.slug}</h3>
                  <p>{category.description || 'Premium quality seeds'}</p>
                  <div className="category-count">
                    {category.varieties_count || 0} Varieties
                  </div>
                  <div className="category-arrow">→</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Our Seeds */}
      <section className="section why-choose" style={{backgroundColor: 'var(--white)'}}>
        <div className="container">
          <h2 className="section-title">Why Choose Our Seeds?</h2>
          <div className="benefits-grid">
            <div className="benefit-card glass-card">
              <div className="benefit-icon">🔬</div>
              <h4>Scientifically Tested</h4>
              <p>All seeds undergo rigorous testing for quality and performance</p>
            </div>
            <div className="benefit-card glass-card">
              <div className="benefit-icon">📦</div>
              <h4>Proper Packaging</h4>
              <p>Sealed packaging to maintain seed viability and freshness</p>
            </div>
            <div className="benefit-card glass-card">
              <div className="benefit-icon">🎯</div>
              <h4>High Germination</h4>
              <p>Guaranteed germination rates for successful cultivation</p>
            </div>
            <div className="benefit-card glass-card">
              <div className="benefit-icon">💰</div>
              <h4>Best Value</h4>
              <p>Competitive pricing without compromising on quality</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section product-cta">
        <div className="container">
          <div className="cta-card glass-card">
            <h2>Need Help Choosing the Right Seeds?</h2>
            <p>Our agricultural experts are here to help you select the best seeds for your farming needs</p>
            <Link to="/contact" className="btn-primary">Contact Our Experts</Link>
          </div>
        </div>
      </section>

      <style>{`
        .loading-state, .error-state, .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #64748b;
        }
        .spinner {
          font-size: 3rem;
          animation: spin 2s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .error-state p { color: #dc2626; margin-bottom: 16px; }
      `}</style>
    </div>
  );
};

export default Products;
